/**
 * SEO utility functions for managing meta tags and structured data
 */

import { SEO_CONFIG, getFullUrl, getFullImageUrl } from '../config/seo.config';

interface MetaTagsConfig {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
}

/**
 * Updates document meta tags for SEO
 */
export const updateMetaTags = (config: MetaTagsConfig) => {
  // Update title
  document.title = config.title;

  // Helper function to update or create meta tag
  const updateMetaTag = (selector: string, content: string) => {
    let tag = document.querySelector(selector) as HTMLMetaElement;
    if (!tag) {
      tag = document.createElement('meta');
      const attrName = selector.includes('property=') ? 'property' : 'name';
      const attrValue = selector.match(/"([^"]+)"/)?.[1];
      if (attrValue) {
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
      }
    }
    tag.content = content;
  };

  // Update meta description
  updateMetaTag('meta[name="description"]', config.description);

  // Update keywords if provided
  if (config.keywords) {
    updateMetaTag('meta[name="keywords"]', config.keywords);
  }

  // Update Open Graph tags
  updateMetaTag('meta[property="og:title"]', config.title);
  updateMetaTag('meta[property="og:description"]', config.description);
  if (config.ogImage) {
    updateMetaTag('meta[property="og:image"]', config.ogImage);
  }
  if (config.ogType) {
    updateMetaTag('meta[property="og:type"]', config.ogType);
  }

  // Update Twitter tags
  updateMetaTag('meta[property="twitter:title"]', config.title);
  updateMetaTag('meta[property="twitter:description"]', config.description);
  if (config.ogImage) {
    updateMetaTag('meta[property="twitter:image"]', config.ogImage);
  }

  // Update canonical URL
  if (config.canonical) {
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = config.canonical;
  }
};

/**
 * Generates JSON-LD structured data for a product
 */
export const generateProductStructuredData = (product: {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  categoryId: string;
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    sku: product.id,
    category: product.categoryId,
    offers: {
      '@type': 'Offer',
      price: product.price.toFixed(2),
      priceCurrency: 'PLN',
      availability: 'https://schema.org/InStock',
      url: getFullUrl(`/product/${product.id}`),
    },
  };
};

/**
 * Generates JSON-LD structured data for breadcrumbs
 */
export const generateBreadcrumbStructuredData = (items: { name: string; url: string }[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

/**
 * Generates JSON-LD structured data for the organization
 */
export const generateOrganizationStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SEO_CONFIG.storeName,
    url: SEO_CONFIG.domain,
    logo: getFullImageUrl(SEO_CONFIG.logoUrl),
    description: SEO_CONFIG.storeDescription,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: SEO_CONFIG.supportEmail,
    },
  };
};

/**
 * Injects structured data into the page
 */
export const injectStructuredData = (data: object | object[]) => {
  // Remove existing structured data script if any
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Create and inject new script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(Array.isArray(data) ? data : [data]);
  document.head.appendChild(script);
};

/**
 * Generates a sitemap entry (for documentation purposes)
 */
export const generateSitemapEntry = (url: string, priority: number = 0.5, changefreq: string = 'weekly') => {
  return {
    url,
    priority,
    changefreq,
    lastmod: new Date().toISOString(),
  };
};
